// Per-game effects layer: particles, screenshake, screen flash, floating numbers.
// One `new Fx()` per client game; call `fx.update(dt)` then `fx.draw(g)` each
// frame, and translate the canvas by `fx.shakeXY()` before drawing the world.
//
// Allocation-light on purpose — particles live in a fixed ring buffer, so a
// long session never grows the heap and a burst during a slow frame can't
// stampede. No `shadowBlur` anywhere; it's the single biggest canvas cost on
// phones.

const MAX_PARTICLES = 260;

interface P {
  live: boolean;
  x: number; y: number;
  vx: number; vy: number;
  g: number;          // gravity px/ms^2
  drag: number;       // per-ms velocity retain (1 = none)
  life: number; ttl: number;
  size: number;
  col: string;
  square: boolean;
  spin: number; rot: number;
}

interface Pop {
  x: number; y: number; vy: number;
  text: string; col: string;
  life: number; ttl: number;
  size: number;
}

export interface BurstOpts {
  count?: number;
  speed?: number;       // base px/ms
  spread?: number;      // 0..1 of a full circle; default 1
  dir?: number;         // centre angle when spread < 1 (radians)
  colour?: string | string[];
  gravity?: number;
  drag?: number;
  size?: number;
  life?: number;        // ms
  square?: boolean;
}

export class Fx {
  private ps: P[] = [];
  private head = 0;
  private pops: Pop[] = [];
  private shakeT = 0;
  private shakeDur = 0;
  private shakeMag = 0;
  private flashCol = "";
  private flashT = 0;
  private flashDur = 0;

  constructor() {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.ps.push({
        live: false, x: 0, y: 0, vx: 0, vy: 0, g: 0, drag: 1,
        life: 0, ttl: 0, size: 2, col: "#fff", square: false, spin: 0, rot: 0,
      });
    }
  }

  burst(x: number, y: number, o: BurstOpts = {}) {
    const n = Math.min(o.count ?? 14, MAX_PARTICLES);
    const speed = o.speed ?? 0.08;
    const spread = (o.spread ?? 1) * Math.PI * 2;
    const dir = o.dir ?? 0;
    const cols = Array.isArray(o.colour) ? o.colour : [o.colour ?? "#FFC53D"];
    for (let i = 0; i < n; i++) {
      const p = this.ps[this.head]!;
      this.head = (this.head + 1) % MAX_PARTICLES;
      const a = dir + (Math.random() - 0.5) * spread;
      const v = speed * (0.35 + Math.random() * 0.9);
      p.live = true;
      p.x = x; p.y = y;
      p.vx = Math.cos(a) * v;
      p.vy = Math.sin(a) * v;
      p.g = o.gravity ?? 0;
      p.drag = o.drag ?? 0.9985;
      p.ttl = p.life = (o.life ?? 520) * (0.7 + Math.random() * 0.6);
      p.size = (o.size ?? 3) * (0.6 + Math.random() * 0.8);
      p.col = cols[(Math.random() * cols.length) | 0]!;
      p.square = !!o.square;
      p.spin = (Math.random() - 0.5) * 0.02;
      p.rot = Math.random() * Math.PI;
    }
  }

  trail(x: number, y: number, colour = "#4D7CFF", size = 2.5) {
    const p = this.ps[this.head]!;
    this.head = (this.head + 1) % MAX_PARTICLES;
    p.live = true;
    p.x = x; p.y = y; p.vx = 0; p.vy = 0; p.g = 0; p.drag = 1;
    p.ttl = p.life = 320;
    p.size = size; p.col = colour; p.square = false; p.spin = 0; p.rot = 0;
  }

  pop(x: number, y: number, text: string, colour = "#fff", size = 20) {
    if (this.pops.length > 24) this.pops.shift();
    this.pops.push({ x, y, vy: -0.055, text, col: colour, life: 900, ttl: 900, size });
  }

  shake(mag: number, ms = 260) {
    if (mag < this.shakeMag && this.shakeT > 0) return;
    this.shakeMag = mag;
    this.shakeDur = this.shakeT = ms;
  }

  flash(colour: string, ms = 200) {
    this.flashCol = colour;
    this.flashDur = this.flashT = ms;
  }

  shakeXY(): { x: number; y: number } {
    if (this.shakeT <= 0) return { x: 0, y: 0 };
    const k = (this.shakeT / this.shakeDur) * this.shakeMag;
    return { x: (Math.random() - 0.5) * 2 * k, y: (Math.random() - 0.5) * 2 * k };
  }

  update(dt: number) {
    const d = Math.min(dt, 40);
    if (this.shakeT > 0) this.shakeT -= d;
    if (this.flashT > 0) this.flashT -= d;
    for (const p of this.ps) {
      if (!p.live) continue;
      p.ttl -= d;
      if (p.ttl <= 0) { p.live = false; continue; }
      p.vy += p.g * d;
      const k = Math.pow(p.drag, d);
      p.vx *= k; p.vy *= k;
      p.x += p.vx * d; p.y += p.vy * d;
      p.rot += p.spin * d;
    }
    for (let i = this.pops.length - 1; i >= 0; i--) {
      const q = this.pops[i]!;
      q.ttl -= d;
      if (q.ttl <= 0) { this.pops.splice(i, 1); continue; }
      q.y += q.vy * d;
      q.vy *= 0.94;
    }
  }

  /** particles + pops. Call inside the shaken transform (world space). */
  draw(g: CanvasRenderingContext2D) {
    for (const p of this.ps) {
      if (!p.live) continue;
      const a = Math.min(1, p.ttl / (p.life * 0.6));
      g.globalAlpha = a;
      g.fillStyle = p.col;
      if (p.square) {
        g.save();
        g.translate(p.x, p.y);
        g.rotate(p.rot);
        g.fillRect(-p.size, -p.size, p.size * 2, p.size * 2);
        g.restore();
      } else {
        g.beginPath();
        g.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        g.fill();
      }
    }
    g.globalAlpha = 1;
    for (const q of this.pops) {
      const a = Math.min(1, q.ttl / 400);
      g.globalAlpha = a;
      g.font = `800 ${q.size}px "Baloo 2", "Nunito", system-ui, sans-serif`;
      g.textAlign = "center";
      g.textBaseline = "middle";
      g.lineWidth = 4;
      g.strokeStyle = "#1B1534";
      g.strokeText(q.text, q.x, q.y);
      g.fillStyle = q.col;
      g.fillText(q.text, q.x, q.y);
    }
    g.globalAlpha = 1;
  }

  /** full-screen flash. Call in screen space (after undoing the shake). */
  drawFlash(g: CanvasRenderingContext2D, cw: number, ch: number) {
    if (this.flashT <= 0) return;
    g.globalAlpha = 0.6 * (this.flashT / this.flashDur);
    g.fillStyle = this.flashCol;
    g.fillRect(0, 0, cw, ch);
    g.globalAlpha = 1;
  }
}
